/**
 * Cat-flavoured chipmunk voice for the tutorial typewriter.
 *
 *   • One short, high-pitched chirp per visible character (skips
 *     spaces and punctuation so the speech doesn't sound like a
 *     machine gun).
 *
 *   • Triangle-wave oscillator with a fast attack + fast decay
 *     envelope (~40 ms total) and a tiny pitch glide so each chirp
 *     has a "meow-let" character rather than reading as a beep.
 *
 *   • A throttle keeps us from queuing more than ~25 chirps/sec —
 *     if the typewriter rate exceeds that, we just skip a chirp.
 *     The voice stays expressive without becoming a buzz.
 *
 *   • Master gain is wired to the same `wol.volume.music` slider as
 *     the background music — the tutorial voice is a UX layer, not
 *     SFX, so it follows the "ronron de fond" volume the player
 *     already chose. Falls back to 0.30 when nothing is stored.
 */

const MUSIC_VOL_KEY = 'wol.volume.music';
const DEFAULT_VOICE_VOLUME = 0.30;
const MAX_CHIRPS_PER_SECOND = 28;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let lastChirpAt = 0;

function readMusicVolume(): number {
  if (typeof window === 'undefined') return DEFAULT_VOICE_VOLUME;
  try {
    const v = window.localStorage.getItem(MUSIC_VOL_KEY);
    if (v !== null) {
      const n = parseFloat(v);
      if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
    }
  } catch { /* localStorage disabled — ignore */ }
  return DEFAULT_VOICE_VOLUME;
}

function ensureCtx(): boolean {
  if (typeof window === 'undefined') return false;
  if (ctx && master) return true;
  const Ctor = (window as Window & {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  }).AudioContext
    ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return false;
  try {
    ctx = new Ctor();
  } catch { return false; }
  master = ctx.createGain();
  master.gain.value = readMusicVolume() * 0.6; // voice quieter than music itself
  master.connect(ctx.destination);
  return true;
}

/**
 * Fire one chirp for the given character. No-op for whitespace /
 * punctuation. Safe to call from inside a typewriter `setTimeout`
 * cascade — the rate limiter keeps the audio graph cheap.
 */
export function speakChar(ch: string): void {
  if (!ch || /[\s.,!?;:—–\-«»"'()]/.test(ch)) return;
  if (!ensureCtx() || !ctx || !master) return;
  const now = ctx.currentTime;
  // Rate-limit: don't queue chirps faster than the cap.
  const minGap = 1 / MAX_CHIRPS_PER_SECOND;
  if (now - lastChirpAt < minGap) return;
  lastChirpAt = now;

  // Pitch: ~800-1400 Hz with a small per-char hash so consonants
  // don't all chirp at the same note. Vowels pitch a hair higher
  // than consonants for a hint of phonetic colour.
  const lower = ch.toLowerCase();
  const isVowel = 'aeiouyàâéèêëîïôùû'.includes(lower);
  const base = isVowel ? 1100 : 900;
  // Cheap deterministic spread based on charCode.
  const spread = ((lower.charCodeAt(0) * 17) % 320);
  const startHz = base + spread;
  const endHz = startHz * 0.86; // tiny downward glide — "ee-oh" feel

  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(startHz, now);
  osc.frequency.exponentialRampToValueAtTime(endHz, now + 0.045);

  // Per-chirp envelope: 5 ms attack, 35 ms decay.
  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, now);
  env.gain.exponentialRampToValueAtTime(0.55, now + 0.005);
  env.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);

  osc.connect(env);
  env.connect(master);
  osc.start(now);
  osc.stop(now + 0.06);
}

/**
 * Resync the master gain to the music volume slider — call this when
 * the player drags the slider while a chirp burst is in flight so the
 * voice ducks immediately.
 */
export function refreshVoiceVolume(): void {
  if (master) master.gain.value = readMusicVolume() * 0.6;
}
